// Countertops/Cabinets — Pricing Contract product selection (pure, testable).
//
// The flooring Pricing Contract wizard keeps a dedicated selected-state array per category
// (selectedCarpet, selectedSheetVinyl, …) and hard-codes render branches per flooring id. Rather
// than duplicate that per countertop category, this module keeps a GENERIC selection map keyed by
// productStructureId, so any number of C/C structures (Cabinet Boxes 255, Countertops 256, Cabinet
// Doors 257, …) reuse the same logic. Detection is driven by the DB `isCountertopCabinet` flag on
// the product-structure list — never by hard-coded ids (the constants are only a fallback).

import {
  CABINET_BOX_ID_DB,
  COUNTER_TOP_ID_DB,
  CABINET_DOOR_ID_DB,
  CABINET_SUBSTRATE_ID_DB,
  THERMOFOIL_ID_DB,
  FIXTURES_ID_DB,
} from "./constants/ProductCatalog";

// Fallback set (only used if the product-structure list hasn't loaded).
export const COUNTERTOP_STRUCTURE_IDS = [
  CABINET_BOX_ID_DB,
  COUNTER_TOP_ID_DB,
  CABINET_DOOR_ID_DB,
  CABINET_SUBSTRATE_ID_DB,
  THERMOFOIL_ID_DB,
  FIXTURES_ID_DB,
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

// The countertop structure ids, resolved from ProductType.valueList via the DB flag.
export function getCountertopStructureIds(valueList) {
  if (Array.isArray(valueList) && valueList.length > 0) {
    const ids = valueList
      .filter((v) => v?.isCountertopCabinet === true)
      .map((v) => Number(v.productStructureId))
      .filter((n) => Number.isFinite(n));
    if (ids.length > 0) return ids;
  }
  return [...COUNTERTOP_STRUCTURE_IDS];
}

// Is `id` a countertop/cabinet product structure? (DB-flag driven, constant fallback.)
export function isCountertopStructureId(id, valueList) {
  const target = num(id);
  if (Number.isNaN(target)) return false;
  return getCountertopStructureIds(valueList).some((s) => Number(s) === target);
}

// --- Generic selection map: { [structureId]: { ids: number[], items: product[] } } ---

export function getStructureSelection(state = {}, structureId) {
  return state?.[structureId] || { ids: [], items: [] };
}

// Toggle a product's selection within its structure bucket.
export function toggleProduct(state = {}, structureId, product = {}) {
  const bucket = getStructureSelection(state, structureId);
  const pid = num(product.productId);
  const already = bucket.ids.some((i) => Number(i) === pid);
  const nextBucket = already
    ? {
        ids: bucket.ids.filter((i) => Number(i) !== pid),
        items: bucket.items.filter((it) => Number(it.productId) !== pid),
      }
    : {
        ids: [...bucket.ids, pid],
        items: [...bucket.items, { ...product }],
      };
  return { ...state, [structureId]: nextBucket };
}

// Update a field (price / gpm / …) on a selected product.
export function setProductField(state = {}, structureId, productId, field, value) {
  const bucket = getStructureSelection(state, structureId);
  const pid = num(productId);
  return {
    ...state,
    [structureId]: {
      ...bucket,
      items: bucket.items.map((it) =>
        Number(it.productId) === pid ? { ...it, [field]: value } : it
      ),
    },
  };
}

export function isProductSelected(state = {}, structureId, productId) {
  return getStructureSelection(state, structureId).ids.some(
    (i) => Number(i) === num(productId)
  );
}

// --- Price <-> GPM (matches BUSINESS_LOGIC §6.2; kept local to stay decoupled) ---

export function gpmFromPrice(cost, price) {
  const p = num(price);
  const c = num(cost);
  if (Number.isNaN(p) || Number.isNaN(c) || p === 0) return "";
  return Math.round(((p - c) / p) * 100 * 100) / 100;
}

export function priceFromGpm(cost, gpm) {
  const g = num(gpm);
  const c = num(cost);
  if (Number.isNaN(g) || Number.isNaN(c) || g >= 100) return "";
  return Math.round(((c * 100) / (100 - g)) * 100) / 100;
}

// Flatten the selection map into the line items the contract save expects.
export function flattenSelections(state = {}) {
  return Object.entries(state).flatMap(([structureId, bucket]) =>
    (bucket?.items || []).map((item) => ({
      productStructureId: Number(structureId),
      productId: item.productId,
      price: item.price,
      gpm: item.gpm,
    }))
  );
}

// --- Multiple-primary selection (Countertops & Cabinet Boxes only) --------------------------
//
// Flooring and the other C/C structures enforce a single primary product per structure. The
// business rule carves out an exception for Countertops and Cabinet Boxes: a customer can mark
// several of these as "primary" at once (e.g. a Kitchen countertop and a Vanity countertop).
// Detection is DB-flag-first (`isMultiplePrimary` on the product-structure row) with the id
// constants as the fallback — mirroring isCountertopStructureId / isCountertopCabinetStore, and
// never hard-coding anything the DB can tell us.
export const MULTI_PRIMARY_STRUCTURE_IDS = [CABINET_BOX_ID_DB, COUNTER_TOP_ID_DB];

export function allowsMultiplePrimary(id, valueList) {
  const target = num(id);
  if (Number.isNaN(target)) return false;
  if (Array.isArray(valueList)) {
    const match = valueList.find((v) => Number(v?.productStructureId) === target);
    if (match && typeof match.isMultiplePrimary === "boolean") {
      return match.isMultiplePrimary;
    }
  }
  return MULTI_PRIMARY_STRUCTURE_IDS.some((s) => Number(s) === target);
}

// Keep only the kit rows (labor / accessory material) still required by at least one of the
// currently-selected products, matched by `kitId`. When one of several selected primary products
// is deselected, kits shared with a remaining product survive, and each kit appears once. Mirrors
// the flooring syncLabor/syncMaterial retention (`laborKitIds.includes(row.kitId)`) as a pure,
// testable helper.
export function retainSharedKits(kitRows = [], selectedProducts = []) {
  if (!Array.isArray(kitRows)) return [];
  const kitIds = new Set(
    (Array.isArray(selectedProducts) ? selectedProducts : [])
      .map((p) => p?.kitId)
      .filter((k) => k !== null && k !== undefined)
  );
  return kitRows.filter((row) => kitIds.has(row?.kitId));
}

// Single-primary enforcement for a product-selection toggle. Returns the productIds of the
// "extra" primaries (every primary after the first) that belong to `toggledStructureId` and
// must be reverted to non-primary — i.e. the structures that still enforce one primary.
//
// Keyed on the TOGGLED item's structure, never the current wizard step: the enforcement effect
// fires again whenever the step's product list changes, so gating on the step (e.g. advancing to
// single-primary Cabinet Doors) would wrongly strip a Countertops/Cabinet Boxes selection that
// was toggled on an earlier step. Countertops & Cabinet Boxes allow multiple primaries, so they
// never produce extras ([]).
export function extraPrimaryProductIdsForToggle(products, toggledStructureId, valueList) {
  if (!Array.isArray(products)) return [];
  if (allowsMultiplePrimary(toggledStructureId, valueList)) return [];
  const sid = num(toggledStructureId);
  if (Number.isNaN(sid)) return [];
  let primaryCount = 0;
  const extras = [];
  products.forEach((p) => {
    if (Number(p?.productStructureId) === sid && p?.isPrimary) {
      primaryCount += 1;
      if (primaryCount > 1) extras.push(p.productId);
    }
  });
  return extras;
}
